// 全局共享数据
import common from '@/common';
import gtag from '@/utils/gtag';
import { useModel } from '@umijs/max';
import { useAsyncEffect, useRequest } from 'ahooks';
import { useEffect, useState } from 'react';
import globalServices, {
  checkZcmMonitorOpen,
  getAllAuthButtons,
  getFeature,
  getLanguages,
  getNoAuthButtons,
  getPwdRuleService,
  getSystemParam,
} from './globalServices';

export default function GlobalModel() {
  const { initialState } = useModel('@@initialState');

  const [includeExpired, setIncludeExpired] = useState<boolean>(false);

  const { data: getLanguagesRes } = useRequest(getLanguages);
  const [isOpenMonitor, setIsOpenMonitor] = useState<boolean>(false);
  const { data: allAuthButtons, run: qryAllAuthButtons } = useRequest(
    getAllAuthButtons,
    {
      manual: true,
    },
  );

  const {
    loading: queryRootLoading,
    data: rootAccountList,
    refreshAsync: rootRefresh,
    runAsync: queryRootAcct,
    mutate: mutateRootAccountList,
  } = useRequest(globalServices?.getRootAcct, {
    manual: true,
  });

  const { data: currencyPrecision, run: querySystemParam } = useRequest(
    getSystemParam,
    {
      manual: true,
    },
  );
  const { data: otpData, run: queryOtpParams } = useRequest(getSystemParam, {
    manual: true,
  });

  const { data: ticketUseAIData, run: queryTicketUseAIDataFn } = useRequest(
    getSystemParam,
    { manual: true },
  );

  // 查询货币类型
  const { data: accessData, run: queryAccessParams } = useRequest(
    getSystemParam,
    { manual: true },
  );

  // 查询校验规则
  const { data: pwdRulesData, run: queryPwdRulesDataFn } = useRequest(
    getPwdRuleService,
    {
      manual: true,
    },
  );

  // 查询权限按钮功能开关
  const { data: currencyTypeData, run: queryCurrencyTypeFn } = useRequest(
    getSystemParam,
    { manual: true },
  );

  // 查询显示精度
  const { data: displayScaleData, run: queryDisplayScale } = useRequest(
    getSystemParam,
    { manual: true },
  );

  // 是否启用查询失效账户
  const { data: includeExpiredAccount, run: queryIncludeExpired } = useRequest(
    getSystemParam,
    { manual: true },
  );

  // 是否启用autopay签约
  const { data: autopayEnable, run: queryAutopayEnable } = useRequest(
    getSystemParam,
    { manual: true },
  );

  // Autopay查询Terms&Condition的URL
  const { data: termsAndConditions, run: queryTermsAndConditions } = useRequest(
    getSystemParam,
    { manual: true },
  );

  // 查询支付传递单位
  const { data: currncyUnitData, run: queryCurrncyUnitData } = useRequest(
    getSystemParam,
    { manual: true },
  );

  // 查询order改约配置项
  const { data: orderChangeAppointData, run: queryOrderChangeAppointData } =
    useRequest(getSystemParam, { manual: true });

  // 查询TT单改约配置项
  const { data: caseChangeAppointData, run: queryCaseChangeAppointData } =
    useRequest(getSystemParam, { manual: true });

  // 查询客户提供的Google Analytics的衡量ID
  const queryMeasurementId = async () => {
    const id = await getSystemParam({
      mask: 'BCARE_MEASUREMENT_ID_FOR_GOOGLE_ANALYTICS',
    });
    return id;
  };

  // 查询最小金额配置项
  const { data: minAmountData, run: queryMinAmountData } = useRequest(
    getSystemParam,
    { manual: true },
  );

  // 是否开启二次业务批量季度开关
  const { data: featureData, run: queryFeature } = useRequest(getFeature, {
    manual: true,
  });

  const [noAuthButtons, setNoAuthButtons] = useState([]);

  const { languageList = [] } = getLanguagesRes || {};

  const { projectCode } = common;

  // 加载 gtag.js,用于GA埋点
  const initGTags = (measurementId: string) => {
    const script = document.createElement('script');
    // 设置 script 的内容为一个自执行函数，用于初始化 gtag.js
    script.innerHTML = `
      (function() {
        // 加载 gtag.js
        const gtagScript = document.createElement('script');
        gtagScript.async = true;
        gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=${measurementId}';
        document.head.appendChild(gtagScript);

        // 初始化 dataLayer
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        
        // 配置 gtag.js
        gtag('js', new Date());
        gtag('config', '${measurementId}');
      })();
    `;
    document.head.appendChild(script);
  };

  useAsyncEffect(async () => {
    if (initialState?.custCode) {
      // 获取根账户树
      await queryRootAcct({
        custcode: initialState?.custCode,
        uniqueAcctId: initialState?.acctId,
        filterType: '',
        filterParam: '',
        ...(includeExpired
          ? {
              includExpFlag: 'Y',
              qryOutstandingFlag: 'Y',
            }
          : {}),
      });
    }
    if (projectCode === 'tm' || projectCode === 'product') {
      if (initialState?.custId || initialState?.userId) {
        // 登录后判断是否打开监控
        const isMonitor = await checkZcmMonitorOpen();
        setIsOpenMonitor(isMonitor || false);
        if (isMonitor) {
          const measurementId = await queryMeasurementId();
          if (measurementId) {
            // 初始化 gtag.js,向Google Analytics上报数据
            initGTags(measurementId);
            // GA埋点-TM
            gtag('event', 'login', {
              action_type: 'Action',
              action_name: 'Login',
            });
          }
        }
      }
    }
  }, [initialState, projectCode, includeExpired]);

  useEffect(() => {
    // 获取配置项
    // 利比亚和PTO没有用到OTP配置项，不需要调
    if (projectCode !== 'lib' && projectCode !== 'pto') {
      queryOtpParams({
        mask: 'BCARE_ENABLE_LOGIN_OTP',
      });
    }
  }, []);

  useEffect(() => {
    // 登录后调季度开关
    if (initialState?.userId) {
      // 配置最小付款金额
      queryMinAmountData({
        mask: 'BCARE_PAYMENT_MINIMUM_PAYMENT',
      });

      // 获取精度
      querySystemParam({
        mask: 'CURRENCY_PRECISION',
      });

      // 是否允许 ticket 菜单使用AI功能
      queryTicketUseAIDataFn({
        mask: 'IS_ENABLE_AI',
      });

      // 查询货币类型
      queryCurrencyTypeFn({
        mask: 'BCARE_CURRENCY_CODE',
      });

      // 是否开启权限按钮开关
      queryAccessParams({
        mask: 'BUTTON_FUNCTION_PAGE_CONFIG_FLAG',
      });

      // 查询展示精度
      queryDisplayScale({
        mask: 'BCARE_CURRENCY_DISPLAY_PRECISION',
      });

      // 是否启用查询失效账户
      queryIncludeExpired({
        mask: 'BCARE_EXP_ACCT_ENABLE',
      });

      // 是否启用autopay签约
      queryAutopayEnable({
        mask: 'BCARE_AUTOPAY_ENABLE',
      });

      // Autopay查询Terms&Condition的URL
      queryTermsAndConditions({
        mask: 'BCARE_AUTOPAY_TNC_URL',
      });

      //
      queryCurrncyUnitData({
        mask: 'BCARE_CURRENCY_UNIT',
      });

      // order改约
      queryOrderChangeAppointData({
        mask: 'BCARE_ORDER_CHANGE_APPOINT_ENABLE',
      });

      // TT单case 改约
      queryCaseChangeAppointData({
        mask: 'BCARE_CASE_CHANGE_APPOINT_ENABLE',
      });

      if (projectCode === 'tm' || projectCode === 'product') {
        // 二次业务批量季度开关
        queryFeature('BSS_ALL_2025Q2');
      }
    }
  }, [initialState]);

  useAsyncEffect(async () => {
    if (initialState?.roles?.roleId) {
      const res = await getNoAuthButtons(initialState?.roles?.roleId);
      setNoAuthButtons(res || []);
      qryAllAuthButtons();
    }
  }, [initialState]);

  return {
    queryRootAcct,
    rootRefresh,
    languageList, // 语言可选列表
    allAuthButtons, // 所有可配置的权限按钮
    noAuthButtons, // 所有没权限的按钮
    rootAccountList, // 根账户列表
    queryRootLoading,
    currencyPrecision: currencyPrecision || 2,
    mutateRootAccountList,
    otpData,
    ticketUseAIData,
    currencyTypeData: currencyTypeData || 'RM', // 货币类型
    pwdRulesData: pwdRulesData || [], // 校验规则
    queryPwdRulesDataFn,
    isOpenMonitor, // 是否打开埋点
    accessData,
    displayScaleData,
    includeExpiredAccount: includeExpiredAccount || 'N', // 是否启用查询失效账户
    includeExpired,
    setIncludeExpired,
    autopayEnable: autopayEnable || 'N',
    termsAndConditions,
    currncyUnitData,
    orderChangeAppointData: orderChangeAppointData || 'N', // 默认为N，N表示不启用，Y表示启用
    caseChangeAppointData: caseChangeAppointData || 'N', // 默认为N，N表示不启用，Y表示启用
    minAmountData: minAmountData || '2', // 默认最小金额为2
    featureData: featureData || '', // 批量季度开关, 返回"1"表示启用，其他返回表示不启用
  };
}
